import { motion } from 'framer-motion';

import { Avatar, AvatarFallback, AvatarImage, cn } from '@llura/ui';

interface CoachAvatarProps {
  name: string | null;
  src: string | null;
  size?: number;
  glow?: boolean;
  /** When true: pulsing aura ring + 3-bar audio waveform overlay. */
  speaking?: boolean;
  className?: string;
}

function initials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function CoachAvatar({
  name,
  src,
  size = 56,
  glow = false,
  speaking = false,
  className,
}: CoachAvatarProps) {
  return (
    <div
      className={cn('relative inline-flex', className)}
      style={{ width: size, height: size }}
    >
      {speaking && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-full border-2 border-secondary/60"
          animate={{ scale: [1, 1.12, 1], opacity: [0.55, 0.9, 0.55] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      <Avatar
        className={cn(
          'border border-border bg-card',
          glow && 'glow-primary',
        )}
        style={{ width: size, height: size }}
      >
        {src && <AvatarImage src={src} alt={name ?? 'Coach'} />}
        <AvatarFallback className="bg-card font-serif text-xl text-primary">
          {initials(name)}
        </AvatarFallback>
      </Avatar>

      {speaking && <WaveformOverlay />}
    </div>
  );
}

function WaveformOverlay() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-1.5 left-1/2 flex -translate-x-1/2 items-end gap-0.5"
    >
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="block w-1 rounded-full bg-accent"
          animate={{ height: ['4px', '12px', '4px'] }}
          transition={{
            duration: 0.7 + i * 0.15,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.12,
          }}
        />
      ))}
    </div>
  );
}
